// EntityDetailsContext — lättviktig context för att öppna
// EntityDetailsDialog från godtycklig descendant (block, group, layer
// + deras context-menyer) utan prop-drilling.
//
// Provider:n håller open-state + valt entity-target. Hook-exporten
// (useEntityDetailsDialog) ligger i en egen fil så fast-refresh inte
// klagar på blandad export-shape.

import { createContext, useCallback, useState, type ReactNode } from 'react';
import type { EntityType } from '@/hooks/useEntityDetails';

interface OpenArgs {
  entityId: string;
  entityType: EntityType;
  entityName?: string;
}

export interface EntityDetailsContextValue {
  open: boolean;
  entityId: string | null;
  entityType: EntityType;
  entityName: string;
  /** Active workflow_id — vital för indicator-hooks som behöver
   *  kunna fråga vilka entities som har details/attachments. */
  workflowId: string | null;
  setWorkflowId: (id: string | null) => void;
  openDetails: (args: OpenArgs) => void;
  closeDetails: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const EntityDetailsContext = createContext<EntityDetailsContextValue | null>(null);

export function EntityDetailsProvider({ children }: { children: ReactNode }) {
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [state, setState] = useState<{
    open: boolean;
    entityId: string | null;
    entityType: EntityType;
    entityName: string;
  }>({ open: false, entityId: null, entityType: 'block', entityName: '' });

  const openDetails = useCallback((args: OpenArgs) => {
    setState({
      open: true,
      entityId: args.entityId,
      entityType: args.entityType,
      entityName: args.entityName ?? '',
    });
  }, []);

  const closeDetails = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
  }, []);

  return (
    <EntityDetailsContext.Provider value={{ ...state, workflowId, setWorkflowId, openDetails, closeDetails }}>
      {children}
    </EntityDetailsContext.Provider>
  );
}
